import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Star, Check, X, MessageSquareHeart } from "lucide-react";

const STATUS = {
  pending: { label: "Pending", cls: "bg-amber-100 text-amber-800 hover:bg-amber-100" },
  approved: { label: "Live on Website", cls: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" },
  rejected: { label: "Rejected", cls: "bg-red-100 text-red-700 hover:bg-red-100" },
};

export default function Reviews() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/reviews").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const moderate = async (r, action) => {
    setBusy(r.id);
    try {
      await api.put(`/reviews/${r.id}`, { action });
      toast.success(action === "approved" ? "Review website par LIVE ho gaya" : "Review reject ho gaya");
      load();
    } catch {
      toast.error("Failed");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-5" data-testid="reviews-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Customer Reviews</h1>
        <p className="text-sm text-slate-500">Website se aaye reviews — approve karo tabhi website par dikhenge.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {items.map((r) => (
          <Card key={r.id} className="p-4 border-slate-200" data-testid={`review-card-${r.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-semibold text-slate-900">{r.name}</div>
                {r.medicine_name && <div className="text-[11px] font-semibold text-emerald-700 mt-0.5" data-testid={`review-med-${r.id}`}>for: {r.medicine_name}</div>}
                <div className="flex items-center gap-0.5 mt-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} className={`w-4 h-4 ${s <= r.rating ? "text-amber-400 fill-amber-400" : "text-slate-200"}`} />
                  ))}
                </div>
              </div>
              <Badge className={STATUS[r.status]?.cls} data-testid={`review-status-${r.id}`}>{STATUS[r.status]?.label || r.status}</Badge>
            </div>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">"{r.message}"</p>
            <div className="text-[11px] text-slate-400 mt-2">{new Date(r.created_at).toLocaleString("en-IN")}</div>
            <div className="flex gap-2 mt-3">
              {r.status !== "approved" && (
                <Button size="sm" onClick={() => moderate(r, "approved")} disabled={busy === r.id} data-testid={`review-approve-${r.id}`} className="h-8 bg-emerald-900 hover:bg-emerald-800">
                  <Check className="w-3.5 h-3.5 mr-1" /> Approve
                </Button>
              )}
              {r.status !== "rejected" && (
                <Button size="sm" variant="outline" onClick={() => moderate(r, "rejected")} disabled={busy === r.id} data-testid={`review-reject-${r.id}`} className="h-8 text-red-600 border-red-200">
                  <X className="w-3.5 h-3.5 mr-1" /> Reject
                </Button>
              )}
            </div>
          </Card>
        ))}
        {items.length === 0 && (
          <Card className="p-10 border-slate-200 text-center lg:col-span-2">
            <MessageSquareHeart className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-400">Abhi koi review nahi aaya.</p>
          </Card>
        )}
      </div>
    </div>
  );
}
